import { createContext, useEffect, useState, FC } from 'react';
import {
  AccountInfo,
  InteractionRequiredAuthError,
  AuthenticationResult,
  InteractionStatus,
} from '@azure/msal-browser';
import Cookies from 'js-cookie';
import { useMsal, useAccount } from '@azure/msal-react';
import { retrieveBarearToken } from 'services/AuthService';
import Spinner from 'components/Spinner';
import { TOKEN_KEY } from 'constants/common';
import axios from 'axios';

export type AuthContextProps = {
  account: AccountInfo;
  isAuthenticated: boolean;
  logout: () => void;
  photoUrl: string | null;
};

const AuthContext = createContext<Partial<AuthContextProps>>({});

const AuthProvider: FC<{ children: JSX.Element }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAuthLoading, setAuthLoading] = useState(false);
  const { accounts, instance, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [responseToken, setResponseToken] = useState<string | null>(null);

  const handleResponse = async (response: AuthenticationResult) => {
    try {
      await retrieveBarearToken(response);
      setResponseToken(response.accessToken);
      setIsAuthenticated(true);
    } catch {
      setIsAuthenticated(false);
    }
  };
  useEffect(() => {
    async function acquireToken() {
      try {
        await instance.initialize();
        if (account) {
          setAuthLoading(true);

          const accessTokenRequest = {
            scopes: ['User.Read', 'api://f3b788ef-b965-433a-ba89-ae071e313683/access_as_user'],
            account,
          };

          instance
            .acquireTokenSilent(accessTokenRequest)
            .then(handleResponse)
            .catch(error => {
              console.error(error);
              if (error instanceof InteractionRequiredAuthError) {
                return instance.acquireTokenPopup(accessTokenRequest);
              }
            })
            .finally(() => {
              setAuthLoading(false);
            });
        }
      } catch (error) {
        console.error(error);
      }
    }

    acquireToken();
  }, [account, instance]);

  useEffect(() => {
    if (!responseToken) return;
    const photoUrl = 'https://graph.microsoft.com/v1.0/me/photo/$value';
    const fetchAndSetPhotoUrl = async () => {
      try {
        const response = await axios.get(photoUrl, {
          headers: { Authorization: `Bearer ${responseToken}` },
          responseType: 'blob',
        });
        const imageUrl = URL.createObjectURL(response.data);
        setPhotoUrl(imageUrl);
      } catch (error) {
        console.error('Error fetching profile picture:', error);
      }
    };

    fetchAndSetPhotoUrl();
    return () => {
      if (photoUrl) URL.revokeObjectURL(photoUrl);
    };
  }, [responseToken, setPhotoUrl]);

  useEffect(() => {
    // The way to log in for automation tests
    if (Cookies.get(TOKEN_KEY)) {
      setIsAuthenticated(true);
    }
  }, [setIsAuthenticated]);

  const logout = () => setIsAuthenticated(false);

  if (inProgress !== InteractionStatus.None || isAuthLoading) {
    return <Spinner spinning />;
  }

  return <AuthContext.Provider value={{ isAuthenticated, logout, photoUrl }}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };
